<script>
	import { debounce } from "throttle-debounce";
	import {
		DataSearchProvider,
		GeoSearchProvider,
		combineSearchProviders,
		stripHTML,
		extractProperties,
	} from "./searchproviders";

	import iconSearch from "./magnify.svg";
	import iconClose from "./close.svg";
	import iconPin from "./pin.svg";
	import iconHouse from "./house.svg";

	export let type; // array that includes "geo", "data" or both
	export let icon;
	export let iconGeo = "pin";
	export let iconData = "house";

	export let dataSearchData = {};
	export let dataSearchKeys = [];
	export let dataSearchGetLabel = (d) => {};
	export let dataSearchGetLat = (d) => d.lat;
	export let dataSearchGetLng = (d) => d.lng;

	export let geoSearchBounds = [];

	export let placeholder = "Suche";
	export let debounceTime = 500;
	export let resultCallback = () => {};
	export let clearCallback = undefined;

	let elemResultsContainer;
	let elemInput;

	let input = "";
	let results = [];
	let state = "search"; // working, close,
	let focus = false;

	const searchIcons = {
		close: iconClose,
		search: iconSearch,
	};

	const resultIcons = {
		pin: iconPin,
		house: iconHouse,
	};

	$: hasInput = input.length !== 0;

	// ///////////////////////////////////////////////////////////
	// setup search provider
	// ///////////////////////////////////////////////////////////

	let spGeo;
	let spData;

	$: if (type.includes("geo")) {
		spGeo = new GeoSearchProvider({
			bounds: geoSearchBounds,
			icon: icon || iconGeo,
		});
	}
	$: if (type.includes("data")) {
		spData = new DataSearchProvider({
			data: dataSearchData?.features ? extractProperties(dataSearchData) : dataSearchData,
			keys: dataSearchKeys,
			getLabel: dataSearchGetLabel,
			getLat: dataSearchGetLat,
			getLng: dataSearchGetLng,
			icon: icon || iconData,
		});
	}

	let searchProvider;
	$: if (type.includes("geo") && type.length === 1) searchProvider = spGeo;
	else if (type.includes("data") && type.length === 1) searchProvider = spData;
	else if (type.includes("data") && type.includes("geo")) searchProvider = combineSearchProviders([spData, spGeo]);

	// ///////////////////////////////////////////////////////////
	// search functions
	// ///////////////////////////////////////////////////////////
	const doSearch = debounce(debounceTime, () => {
		search();
	});

	const search = () => {
		const q = input;
		const getResults = q ? searchProvider.search(q) : Promise.resolve([]);

		getResults.then((r) => {
			results = r;
			state = results.length ? "close" : "search";
		});
	};

	export const stopSearch = (label) => {
		results = [];
		focus = false;

		if (label) {
			state = "close";
			input = stripHTML(label);
		} else {
			state = "search";
			input = "";
		}
	};

	export const highlightSubstring = (str, substring, onlyFirst = true) => {
		// Create a case-insensitive regex pattern to find the input in the label
		const regex = new RegExp(`(${substring})`, onlyFirst ? "i" : "ig");

		// Replace all occurrences of the input with the input enclosed in <b> tags
		return str.replace(regex, "<b>$1</b>");
	};
</script>

<div
	class="tsg-search"
	class:focus
	class:hasInput
	on:focusin={() => {
		requestAnimationFrame(() => {
			focus = true;
		});
		elemResultsContainer.scrollTop = 0;
	}}
	on:focusout={() => {
		requestAnimationFrame(() => {
			focus = false;
		});
	}}
>
	<div class="search-bar">
		<input
			class="input"
			bind:this={elemInput}
			bind:value={input}
			type="text"
			placeholder=""
			autocomplete="off"
			on:keydown={(e) => {
				if (e.key === "Escape") stopSearch();
			}}
			on:keyup={(e) => {
				if (e.key === "Escape") return;
				state = "working";
				doSearch();
			}}
		/>
		{#if !focus && !hasInput}
			<div class="placeholder">{placeholder}</div>
		{/if}
		<div
			class={`search-icon icon-${state}`}
			style:background-image={searchIcons[state]}
			on:click={() => {
				if (state === "search") {
					focus = true;
					elemInput.focus();
				}
				if (state === "close") {
					if (clearCallback) clearCallback();
					stopSearch();
				}
			}}
		>
			{#if state === "working"}
				<div class="icon-inner" />
			{:else}
				<img src={searchIcons[state]} alt="" />
			{/if}
		</div>
	</div>
	<div
		class="results-container"
		tabindex="1"
		bind:this={elemResultsContainer}
		class:empty={results.length === 0}
		on:click={(e) => e.stopPropagation()}
	>
		<div class="results" tabindex="2">
			{#each results as r, i ((r.label, i))}
				{@const highlighted = highlightSubstring(r.label, input)}
				<div
					class="result"
					on:click={(e) => {
						e.stopPropagation();
						resultCallback(r);
						stopSearch(r.label);
					}}
				>
					<div class="result-icon">
						<img src={resultIcons[r.icon]} alt="" />
					</div>
					<div class="result-label">{@html highlighted}</div>
				</div>
			{/each}
		</div>
	</div>
</div>

<style lang="scss">
	$c-white: #ffffff;
	$c-black1: #1f1f1f;
	$c-black2: #424242;
	$c-grey: #cacaca;

	$s-bar-height: 41px;

	.tsg-search {
		position: relative;

		@include scrollbar;
	}

	.search-bar {
		height: $s-bar-height;
		width: 100%;
		position: relative;
		transition: border 0.4s ease;
		box-sizing: border-box;
		border-radius: 5px;
		border-top: 3px solid #e3e2e9;
		background: #e3e2e9;
		box-shadow: 0px 0px 13px 0px rgba(255, 255, 255, 0.5);
		z-index: 1002;

		&:hover {
			background: #f5f5f5;
			border-color: #f5f5f5;
		}

		&:focus-within {
			background: #fff !important;
			border-color: #fff;
		}
	}

	.input,
	.placeholder {
		position: absolute;
		left: 15px;
		top: 50%;
		transform: translateY(-50%);
		font-family: inherit;
		background: transparent;
		border: none;
		outline: 0;
		box-shadow: none;
		margin: 0;
		padding: 0;
		line-height: 16px;
		white-space: nowrap;
		font-weight: normal;
		width: calc(100% - 55px);
		letter-spacing: 0.15px;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.input {
		color: $c-black2;
	}

	.placeholder {
		color: $c-black2;
		pointer-events: none;
	}

	.search-icon {
		display: flex;
		justify-content: center;
		align-items: center;
		width: #{$s-bar-height - 2px};
		height: #{$s-bar-height - 4px};
		position: absolute;
		right: 0;
		top: 1px;
		transition: background-color 0.3s ease;
		cursor: pointer;

		&:hover {
			background: #aeaeae80;
		}

		img {
			width: 20px;
			height: 20px;
		}

		&.icon-working {
			pointer-events: none;

			.icon-inner {
				width: 70%;
				height: 70%;
				position: absolute;
				left: 15%;
				top: 15%;
				border-radius: 50%;
				background: $c-black2;
				background: linear-gradient(to right, $c-black2 10%, $c-white 42%);
				animation: rotate 0.9s infinite linear;
				pointer-events: none;

				&::before {
					width: 50%;
					height: 50%;
					background: $c-white;
					border-radius: 100% 0 0 0;
					position: absolute;
					top: 0;
					left: 0;
					content: " ";
				}

				&::after {
					background: $c-white;
					width: 75%;
					height: 75%;
					border-radius: 50%;
					content: "";
					margin: auto;
					position: absolute;
					top: 0;
					left: 0;
					bottom: 0;
					right: 0;
				}
			}
		}
	}

	@keyframes rotate {
		0% {
			transform: rotate(0deg);
		}

		100% {
			transform: rotate(360deg);
		}
	}

	.results-container {
		max-height: 300px;
		overflow-y: scroll;
		display: none;
		width: calc(100% + 24px);
		box-sizing: border-box;
		background: rgba($main-blue, 0.98);
		backdrop-filter: blur(10px);
		-webkit-backdrop-filter: blur(10px);
		position: absolute;
		z-index: 1001;
		// box-shadow: 0px 2px 9px 0px rgba(0, 0, 0, 0.15);
		box-shadow: 0px 0px 15px rgba(255, 255, 255, 0.12);
		border-radius: 5px;
		left: -12px;

		@include scrollbar;
	}

	.results {
		padding: 4px 10px 4px 15px;

		&:empty {
			display: none;
		}
	}

	.focus {
		.search-bar {
			// border: 1px solid #00000066;
		}

		.results-container {
			display: block;

			&.empty {
				display: none;
			}
		}
	}

	.result {
		font-size: 16px;
		box-sizing: border-box;
		text-overflow: ellipsis;
		width: 100%;
		padding: 10px 0px;
		cursor: pointer;
		opacity: 0.7;
		border-radius: 4px;
		text-align: left;
		display: flex;
		align-items: center;

		&:last-child {
			border-bottom: none;
		}

		&:hover {
			background: rgba($white, 0.3);
			opacity: 1;
		}
	}

	.result-icon {
		display: none;
		margin-right: 9px;
		margin-bottom: 5px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		position: relative;

		img {
			width: 24px;
			height: 24px;
		}
	}

	.result-label {
		display: inline;

		b {
			font-weight: $bold;
		}
	}

	// CUSTOMIZATIONS

	.tsg-search {
		width: 100%;
		// margin: 12px 0;
		transition: top 0.3s linear;
	}

	.search-icon {
		width: $s-bar-height - 8px;
		height: $s-bar-height - 8px;
		right: 2px;
		transition: background-color 0.1s linear;
		border-radius: 100px;
	}

	.placeholder {
		color: #767676;
		font-style: italic;
	}

	.focus,
	.hasInput {
	}
</style>
